import React, { useCallback, useMemo, useState } from 'react'
import { createSelector } from '@reduxjs/toolkit'
import { useAppSelector } from '../../../../store/hooks'
import {
  selectCrossClientResolversInFlight,
  selectCrossClientResultBuckets,
  selectCrossClientResultsExecution,
} from '../../../../store/crossClient/crossClientResults'
import { type CrossClientQueryExecution } from '../../../../models/CrossClient'
import { CCQ_CARD_ORDER, cardCounts, ccqCriteria, ccqCriterionClauses, countReachedByResolvers, sumCounts, type CcqCard } from './ccqResultView'
import { useWaveTween } from './useWaveTween'
import { CcqPhases, CcqRunContext, CcqRunDisplayContext, deriveCcqPhase, type CcqRun } from './useCcqRun'

const selectCcqCardCounts = createSelector(
  [selectCrossClientResultBuckets, selectCrossClientResultsExecution, selectCrossClientResolversInFlight],
  (buckets, execution, inFlight) => cardCounts(buckets, execution, inFlight),
)

interface CcqRunProviderProps {
  execution: CrossClientQueryExecution
}

export const CcqRunProvider: React.FC<React.PropsWithChildren<CcqRunProviderProps>> = ({ execution: routedExecution, children }) => {
  const storedExecution = useAppSelector(selectCrossClientResultsExecution)
  const execution = storedExecution?.id === routedExecution.id ? storedExecution : routedExecution
  const inFlight = useAppSelector(selectCrossClientResolversInFlight)
  const counts = useAppSelector(selectCcqCardCounts)
  const buckets = useAppSelector(selectCrossClientResultBuckets)
  const bucketTotal = buckets.reduce((sum, bucket) => sum + bucket.count, 0)
  const checked = sumCounts(counts)
  const phase = deriveCcqPhase(execution, inFlight, checked)
  const isDone = phase === CcqPhases.DONE

  const [openedFinished] = useState(isDone)
  const [statusCollapsed, setStatusCollapsed] = useState(isDone)
  const collapseStatus = useCallback(() => {
    setStatusCollapsed(true)
  }, [])

  const landed = useMemo(() => ({ checked, counts }), [checked, counts])
  const display = useWaveTween(landed, !openedFinished)
  const unlockedKey = CCQ_CARD_ORDER.filter((card) => display.counts[card] > 0).join(',')
  const unlockedCards = useMemo(
    () => Object.fromEntries(unlockedKey === '' ? [] : unlockedKey.split(',').map((card) => [card, true])) as Partial<Record<CcqCard, true>>,
    [unlockedKey],
  )

  const { decomposition, nnfDecomposition } = execution
  const clauses = useMemo(() => ccqCriterionClauses({ decomposition, nnfDecomposition }), [decomposition, nnfDecomposition])
  const criteria = useMemo(() => ccqCriteria(clauses), [clauses])
  const total = execution.resultCount > 0 ? execution.resultCount : bucketTotal
  const reached = countReachedByResolvers(buckets)
  const stoppedAfter = isDone && execution.canceledAt && reached < total ? reached : null

  const value = useMemo<CcqRun>(
    () => ({
      execution,
      clauses,
      criteria,
      phase,
      isDone,
      hasCounts: checked > 0 || isDone,
      counts,
      unlockedCards,
      total,
      stoppedAfter,
      statusCollapsed,
      collapseStatus,
    }),
    [execution, clauses, criteria, phase, isDone, checked, counts, unlockedCards, total, stoppedAfter, statusCollapsed, collapseStatus],
  )

  return (
    <CcqRunContext.Provider value={value}>
      <CcqRunDisplayContext.Provider value={display}>{children}</CcqRunDisplayContext.Provider>
    </CcqRunContext.Provider>
  )
}
